import { FC } from 'react'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import {
  CargoType,
  IconPaths,
  ModalSize,
  OptionsHelper,
  Permission,
  showAlertLoading,
  showConfirmAlert,
  showError,
} from '../../../../../../../_metronic/helpers'
import { useQueryResponse } from '../../core/QueryResponseProvider'
import {
  getEnderecoByFuncionario,
  getFuncionarioById,
  getLotacoesByFuncionario,
  toggleStatusFuncionario,
} from '../../core/_requests'
import { CertificadoUpload } from '../../components/modal/CertificadoUpload'
import { ConfirmarContaModal } from '../../components/confirmar-conta'
import { RowActions, useModal, useToast } from '../../../../../../components'
import { FuncionarioList } from '../../core/_models'
import { FuncionarioModal } from '../../components/modal/FuncionarioModal'
import { usePermissaoLotacaoLeitura } from '../../core/_lotacaoPermissao'
import { LotacoesModal } from '../../components/modal/LotacoesModal'
import {
  alterarSenha,
  getUserByPessoa,
  getUsuarioByPessoaId,
  inativarSenha,
} from '../../../../../auth/core/_requests'
import { getUnidades } from '../../../../unidades/unidades-list/core/_requests'
import { useAuth } from '../../../../../auth'

type Props = {
  funcionario: FuncionarioList
}

export const FuncionarioActionsCell: FC<Props> = ({ funcionario }) => {
  const { showModal } = useModal()
  const { showToast } = useToast()
  const { currentUser } = useAuth()
  const navigate = useNavigate()
  const { refetch } = useQueryResponse()
  const nome = funcionario.pessoa?.nome || 'Funcionário'
  const isMedico = funcionario.cargo === CargoType.Medico

  const editFuncionario = () => {
    showModal({
      title: 'Editar Funcionário',
      content: <FuncionarioModal />,
      fetchers: {
        funcionario: () => getFuncionarioById(funcionario.id),
        endereco: async () => {
          const data = await getEnderecoByFuncionario(funcionario.id)
          return data[0]
        },
        usuario: () => getUserByPessoa(funcionario.pessoa.id),
        unidades: async () => {
          const resp = await getUnidades(
            `page=0&size=100&search=&idEmpresa=${funcionario.empresa?.id}`
          )
          return OptionsHelper.createOptions(resp.data || [], 'nome', 'id')
        },
      },
    })
  }

  const changePassword = async () => {
    try {
      const isConfirmed = await showConfirmAlert({
        title: 'Redefinir a senha?',
        text: 'A nova senha será enviada para o e-mail do usuário.',
        icon: 'info',
        confirmButtonText: 'Sim, redefinir',
        cancelButtonText: 'Cancelar',
      })
      if (!isConfirmed) return
      const { message } = await alterarSenha(Number(funcionario.pessoa.usuario?.id))
      showToast({ variant: 'success', title: message })
    } catch (error) {
      showError(error)
    }
  }

  const inativarPassword = async () => {
    try {
      const isConfirmed = await showConfirmAlert({
        title: 'Inativar senha?',
        text: 'A senha será inativada, ao logar ele precisará redefinir a senha.',
        icon: 'info',
        confirmButtonText: 'Sim, inativar',
        cancelButtonText: 'Cancelar',
      })
      if (!isConfirmed) return
      const { message } = await inativarSenha(Number(funcionario.pessoa.usuario?.id))
      showToast({ variant: 'success', title: message })
    } catch (error) {
      showError(error)
    }
  }

  const openCertificadoModal = () => {
    showModal({
      title: 'Informar Certificado',
      content: <CertificadoUpload />,
      size: ModalSize.ExtraLarge,
      fetchers: {
        usuario: () => getUsuarioByPessoaId(funcionario.pessoa.id),
      },
    })
  }

  const openLotacoesModal = () => {
    showModal({
      title: 'Lotações do Profissional',
      description: `Integração e-SUS${funcionario.nome ? ` · ${funcionario.nome}` : ''}`,
      content: (
        <LotacoesModal funcionarioId={Number(funcionario.id)} empresaId={funcionario.empresa?.id} />
      ),
      fetchers: {
        lotacoes: () => getLotacoesByFuncionario(Number(funcionario.id)),
      },
    })
  }

  const confirmAccount = () => {
    showModal({
      title: 'Confirmar Conta',
      content: <ConfirmarContaModal id={funcionario.id} />,
      fetchers: {
        funcionario: () => getFuncionarioById(funcionario.id),
      },
    })
  }

  const setStatus = async (ativo: boolean) => {
    try {
      showAlertLoading()
      await toggleStatusFuncionario(Number(funcionario.id), ativo)
      Swal.close()
      showToast({ variant: 'success', title: `${nome} foi ${ativo ? 'ativado' : 'inativado'}.` })
      refetch()
    } catch (error) {
      showError(error)
    }
  }

  // Inativar pede confirmação; ativar é reversível e não pede.
  const askInativar = async () => {
    const confirmed = await showConfirmAlert({
      title: 'Inativar funcionário?',
      text: `${nome} ficará inativo. Os dados e o histórico continuam guardados, e você pode reativá-lo quando quiser.`,
      confirmButtonText: 'Inativar',
      cancelButtonText: 'Cancelar',
      danger: true,
    })
    if (confirmed) await setStatus(false)
  }

  const showValores =
    isMedico &&
    (currentUser?.grupo?.nome === Permission.SuperAdmin ||
      currentUser?.grupo?.nome === Permission.SuperGerente)

  // Espelha os grupos autorizados para Lotacao:read em permission.json (backend)
  const showLotacoes = usePermissaoLotacaoLeitura()

  return (
    <RowActions
      itemName={nome}
      primaryAction={{ label: 'Editar', onClick: editFuncionario, icon: IconPaths.Pen }}
      items={[
        { label: 'Alterar senha', onClick: changePassword },
        { label: 'Inativar senha', onClick: inativarPassword },
        { label: 'Certificado', onClick: openCertificadoModal, hidden: !isMedico },
        {
          label: 'Confirmar conta',
          onClick: confirmAccount,
          hidden: !!funcionario?.pessoa?.usuario?.confirmado,
        },
        {
          label: 'Gerenciar valores',
          onClick: () =>
            navigate(`financeiro/${funcionario.id}`, { state: { nome: funcionario.pessoa.nome } }),
          hidden: !showValores,
        },
        { label: 'Lotações (e-SUS)', onClick: openLotacoesModal, hidden: !showLotacoes },
        funcionario.ativo
          ? { label: 'Inativar', onClick: askInativar, danger: true, divider: true }
          : { label: 'Ativar', onClick: () => setStatus(true), divider: true },
      ]}
    />
  )
}
